import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react"; // Modern icons
import { CategoryAPI } from "../../api";
import { SectionHeading } from "../common";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const ServiceSlider = () => {
	const prevRef = useRef(null);
	const nextRef = useRef(null);
	const [categories, setCategories] = useState([]);

	useEffect(() => {
		CategoryAPI.getAllCategories()
			.then((res) => setCategories(res.data))
			.catch((err) => console.error(err));
	}, []);

	return (
		<section className="max-w-7xl mx-auto px-4">
			{/* Header Area */}
			<div className="flex flex-row justify-between items-center mb-5 gap-6 lg:px-10 p-1">
				<div className="space-y-2">
					<SectionHeading
						title={"Our Services"}
						subtitle={"Service Insight"}
						// description={`Tailored logistics solutions designed to move your business forward with precision and care.`}
					/>
				</div>

				{/* Custom Navigation Buttons */}
				<div className="flex  gap-3">
					<button
						ref={prevRef}
						className="w-12 h-12 rounded-full border-2 border-brand-500 text-brand-500 flex items-center justify-center ha:bg-brand-500 ha:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed group"
					>
						<ChevronLeft className="gh:-translate-x-1 transition-transform" />
					</button>
					<button
						ref={nextRef}
						className="w-12 h-12 rounded-full border-2 border-brand-500 text-brand-500 flex items-center justify-center ha:bg-brand-500 ha:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed group"
					>
						<ChevronRight className="gh:translate-x-1 transition-transform" />
					</button>
				</div>
			</div>

			{/* Slider implementation */}
			<Swiper
				modules={[Navigation, Autoplay, Pagination]}
				onInit={(swiper) => {
					swiper.params.navigation.prevEl = prevRef.current;
					swiper.params.navigation.nextEl = nextRef.current;
					swiper.navigation.init();
					swiper.navigation.update();
				}}
				autoplay={false}
				breakpoints={{
					// When window width is >= 1024px
					1024: {
						slidesPerView: 4,
					},
					// When window width is >= 768px
					768: {
						slidesPerView: 2,
					},
					// When window width is < 768px
					0: {
						slidesPerView: 1,
					},
				}}
				spaceBetween={30}
				className="lg:p-10! p-1!"
			>
				{categories.map((category, index) => (
					<SwiperSlide key={index} className="h-auto">
						<Link
							to={`/services/${category.slug}`}
							className="block group h-full no-underline"
						>
							<div className="relative h-full bg-white rounded-2xl p-6 shadow-sm border border-gray-300 transition-all duration-500 gh:shadow-2xl gh:-translate-y-2 gh:border-brand-500/20">
								{/* Icon/Image Container */}
								<div className="w-16 h-16 mb-6 rounded-xl bg-brand-50 flex items-center justify-center transition-colors duration-500 gh:bg-brand-500">
									<img
										src={category.icon_url}
										alt=""
										className="w-10 h-10 object-contain gh:brightness-0 gh:invert transition-all"
									/>
								</div>

								<h3 className="text-xl font-bold text-gray-900 mb-3 gh:text-brand-600 transition-colors">
									{category.title}
								</h3>

								<p className="text-gray-500 text-sm leading-relaxed mb-6 line-clamp-3">
									{category.short_description}
								</p>

								{/* "Read More" micro-interaction */}
								<div className="flex items-center text-brand-600 font-bold text-sm gap-2 mt-auto">
									<span>Explore Services</span>
									<ArrowRight
										size={16}
										className="transition-transform gh:translate-x-2"
									/>
								</div>

								{/* Decorative background element */}
								<div className="absolute top-0 right-0 w-24 h-24 bg-brand-500/5 rounded-bl-full -z-10 transition-all gh:bg-brand-500/10" />
							</div>
						</Link>
					</SwiperSlide>
				))}
			</Swiper>
		</section>
	);
};

export default ServiceSlider;
